/**
 * PORTED — RO-3-03 / #44296.
 * Origin: `excellent/app/issues/page.tsx` (REWRITE).
 *
 * Changes:
 *   projects/db + reviewInbox + buildWorkExtras → `useApi(modes.issuesBoard)`
 *   setStatus / moveTaskToStatus → `apiCall(taskActions.*)` (RO-3-03 endpoints)
 *   CreateMenu → client/shared/chrome/create-menu
 *   UnifiedIssuesList → client/shared/issues/unified-list
 *   metadata → dropped (SPA has one static title)
 *
 * `controlsHoisted` / `headerExternal` keep their Mode-mount semantics so
 * Work ▸ Issues and the standalone `/issues` route share one body.
 */

import {
  BoundedScroll,
  Card,
  CardContent,
  IssuesBoard,
  TasksHeader,
  resolveTasksState,
  type BoardExtra,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import { modes } from "@excellent/ui/api/contracts/modes";
import { taskActions } from "@excellent/ui/api/contracts/task-actions";
import { UnifiedIssuesList, type ReviewVerdict } from "../issues/unified-list";
import { CreateMenu } from "../chrome/create-menu";
import type { ScopedTask, ViewScope } from "@/projects/types";
import type { WorkItem } from "@/work/types";

export interface IssuesPageProps {
  searchParams: Record<string, string | undefined>;
  controlsHoisted?: boolean;
  headerExternal?: boolean;
}

export function IssuesPage({
  searchParams,
  controlsHoisted,
  headerExternal,
}: IssuesPageProps) {
  const state = resolveTasksState(searchParams);
  const { data, isLoading, error, mutate } = useApi(modes.issuesBoard, {
    query: {
      project: state.projectSlug,
      status: state.statuses.length ? state.statuses.join(",") : undefined,
      assignee: state.assignee,
      range: state.range,
      from: state.from,
      to: state.to,
      view: state.view,
      section: state.section,
      empty: state.showEmpty ? "1" : undefined,
      q: searchParams.q,
      dept: searchParams.dept,
      mine: searchParams.mine,
    },
  });

  if (error) {
    return (
      <p className="px-6 type-body text-destructive">
        {error.message ?? "Failed to load issues."}
      </p>
    );
  }
  if (isLoading || !data) {
    return <p className="px-6 type-body text-muted-foreground">Loading…</p>;
  }

  const scope = data.scope as ViewScope;
  const statusCounts = data.statusCounts as Record<string, number>;
  const assignees = data.assignees as string[];
  const projects = data.projects as { slug: string; name: string }[];
  const byStatus = data.byStatus as Record<string, ScopedTask[]>;
  const extras = data.extras as WorkItem[];
  const verdicts = data.verdicts as ReviewVerdict[];
  const effectiveStatuses = data.effectiveStatuses as string[];

  const header = headerExternal ? null : (
    <TasksHeader
      scope={scope}
      assignees={assignees}
      projects={projects}
      section={state.section}
      view={state.view}
      range={state.range}
      from={state.from}
      to={state.to}
      statuses={state.statuses}
      assignee={state.assignee}
      projectSlug={state.projectSlug}
      showEmpty={state.showEmpty}
      counts={statusCounts}
      inCard={state.view !== "board"}
      parts={controlsHoisted ? "status" : undefined}
      createSlot={
        controlsHoisted ? undefined : (
          <CreateMenu
            key="create-menu"
            defaultProjectSlug={
              scope.kind === "project" ? scope.projectSlug : undefined
            }
          />
        )
      }
    />
  );

  const boardActions = {
    moveTaskToStatus: async (
      taskScope: { projectSlug: string; sectionSlug: string },
      taskId: number,
      status: string,
      beforeTaskId: number | null,
      afterTaskId: number | null,
    ) => {
      await apiCall(taskActions.moveTaskToStatus, {
        body: {
          projectSlug: taskScope.projectSlug,
          sectionSlug: taskScope.sectionSlug,
          taskId,
          status,
          beforeTaskId,
          afterTaskId,
        },
      });
      void mutate();
    },
    setStatus: async (
      taskScope: { projectSlug: string; sectionSlug: string },
      taskId: number,
      status: string,
    ) => {
      await apiCall(taskActions.setStatus, {
        body: {
          projectSlug: taskScope.projectSlug,
          sectionSlug: taskScope.sectionSlug,
          taskId,
          status,
        },
      });
      void mutate();
    },
  };

  return (
    <div className="flex flex-col">
      <div className="@container/page flex flex-col gap-8">
        {state.view === "review" ? (
          (() => {
            const reviewTasks = byStatus.review ?? [];
            const list = (
              <UnifiedIssuesList
                tasks={reviewTasks}
                extras={[]}
                verdicts={verdicts}
                scope={scope}
                empty="Nothing to review — everything's verified."
                sectioned={headerExternal}
              />
            );
            return headerExternal ? (
              <div className="-mx-5">{list}</div>
            ) : (
              <Card className="gap-0 overflow-hidden py-0">
                <CardContent className="px-0">
                  {header}
                  {list}
                </CardContent>
              </Card>
            );
          })()
        ) : state.view === "board" ? (
          <>
            {header}
            <BoundedScroll>
              {(() => {
                const boardExtras: BoardExtra[] =
                  scope.kind === "workspace"
                    ? extras.map((e) => ({
                        id: e.id,
                        title: e.title,
                        why: e.why,
                        intent: e.intent,
                        taskId: e.taskId,
                        href: e.href,
                      }))
                    : [];
                return (
                  <IssuesBoard
                    scope={scope}
                    byStatus={byStatus}
                    actions={boardActions}
                    extras={boardExtras}
                  />
                );
              })()}
            </BoundedScroll>
          </>
        ) : (
          (() => {
            const flat = effectiveStatuses.flatMap((s) => byStatus[s] ?? []);
            const listExtras =
              scope.kind === "workspace" && effectiveStatuses.includes("todo")
                ? extras
                : [];
            const listVerdicts =
              scope.kind === "workspace" && effectiveStatuses.includes("review")
                ? verdicts
                : [];
            const list = (
              <UnifiedIssuesList
                tasks={flat}
                extras={listExtras}
                verdicts={listVerdicts}
                scope={scope}
                empty="No issues match your filters."
                sectioned={headerExternal}
              />
            );
            return headerExternal ? (
              <div className="-mx-5">{list}</div>
            ) : (
              <Card className="gap-0 overflow-hidden py-0">
                <CardContent className="px-0">
                  {header}
                  {list}
                </CardContent>
              </Card>
            );
          })()
        )}
      </div>
    </div>
  );
}
